import { IUsageTypeResSchema, UsageTypeStatusEnum } from '@monetize/types/app';
import { FilterType } from '@monetize/types/common';
import {
  AppColumnDef,
  createColumnHelper,
} from '../../../components/Monetize/DataTableNew/appTable';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import {
  useGetListData,
  usePerformEntityAction,
} from '../../../api/queryUtils';
import {
  MHStack,
  MPageContainer,
  MPageSearchInput,
} from '../../../components/Monetize';
import DataTableActions from '../../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import {
  ACTION_COLUMN_KEY,
  createActionColumn,
} from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import {
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  nameWithIdBodyTemplate,
  textBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { ROUTES } from '../../../constants';
import { getUsageTypeEditRoute } from '../../../constants/routes';
import { USAGE_TYPE_STATUS_DISPLAY } from '../../../constants/usageTypes';
import { useSettingsReadOnlyRule } from '../../../hooks/useSettingsReadOnlyRule';
import {
  tableFilterSelector,
  tablePagerSelector,
  tableSearchTermSelector,
} from '../../../store/global.store';
import {
  FilterStateKeys,
  getDefaultFilter,
  getDefaultPager,
} from '../../../store/store.types';
import {
  buildFilterParamsRequestObject,
  getFiltersApplied,
  getIsTrulyEmptyList,
  transformTableFilterValue,
} from '../../../utils';
import PageHeader from '../components/PageHeader';
import TableHeader from '../components/TableHeader';

const columnHelper = createColumnHelper<IUsageTypeResSchema>();

/**
 * Generates base column definitions for the usage types table
 * @param navigate - React Router navigate function
 * @param actionBodyTemplate - Function to render action menu in each row
 */
const getColumnsBase = (
  actionBodyTemplate: (rowData: IUsageTypeResSchema) => React.ReactNode,
): AppColumnDef<IUsageTypeResSchema, any>[] => [
  columnHelper.accessor('name', {
    ...getColHeaderWithSize('Name', 400),
    cell: nameWithIdBodyTemplate<IUsageTypeResSchema, unknown>({
      idProp: 'id',
      nameProp: 'name',
      idLinkFn: (id) => getUsageTypeEditRoute(id),
    }),
    enableSorting: true,
    sortFn: 'text',
  }),
  columnHelper.accessor('description', {
    ...getColHeaderWithSize('Description', 400),
    cell: textBodyTemplate(),
    enableSorting: false,
  }),
  columnHelper.accessor('unitName', {
    ...getColHeaderWithSize('Unit Name', 300),
    ...getColHeaderFilters('TEXT'),
    cell: textBodyTemplate(),
    enableSorting: false,
  }),
  columnHelper.accessor('status', {
    ...getColHeaderWithSize('Status', 150),
    ...getColHeaderFilters('SET', {
      predefinedFilterValues: getColumnFilterValuesForEnum(
        UsageTypeStatusEnum,
        USAGE_TYPE_STATUS_DISPLAY,
      ),
    }),
    cell: enumDisplayBodyTemplate({
      displayMap: USAGE_TYPE_STATUS_DISPLAY,
    }),
    enableSorting: true,
    sortFn: 'text',
  }),
  columnHelper.accessor('modifyDate', {
    header: 'Updated',
    ...getColHeaderFilters('DATE'),
    cell: dateTimeBodyTemplate(),
    size: 180,
    enableSorting: true,
  }),
  columnHelper.accessor('createDate', {
    header: 'Created',
    ...getColHeaderFilters('DATE'),
    cell: dateTimeBodyTemplate(),
    size: 180,
    enableSorting: true,
  }),
  createActionColumn<IUsageTypeResSchema>(actionBodyTemplate),
];

const UsageTypeListPage = () => {
  const navigate = useNavigate();
  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(FilterStateKeys.USAGE_TYPE_LIST),
  );
  const tableRef = useRef<DataTableRef>(null);

  const [pager, setPager] = useAtom(
    tablePagerSelector(FilterStateKeys.USAGE_TYPE_LIST),
  );

  const [searchTerm, setSearchTerm] = useAtom(
    tableSearchTermSelector(FilterStateKeys.USAGE_TYPE_LIST),
  );

  const [filters, setFilters] = useState<FilterType[]>(initialFilters);
  const [searchKey] = useState<string>('name');
  const filterParams = useMemo(
    () =>
      buildFilterParamsRequestObject(
        transformTableFilterValue(filters),
        searchTerm,
        searchKey,
      ),
    [filters, searchTerm, searchKey],
  );

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  const {
    data: usageTypeList,
    isLoading,
    isFetched,
    isRefetching,
    refetch,
  } = useGetListData<IUsageTypeResSchema>('productCatalogUsageTypes', {
    config: pager,
    filters: filterParams,
  });

  // using mutateAsync so we can show a loading indicator on the menu while action is executing
  const { mutateAsync: doActivateDeactivate } =
    usePerformEntityAction<IUsageTypeResSchema>('productCatalogUsageTypes', {
      meta: { showErrorToast: true },
    });

  const loading = isLoading || (isRefetching && !isFetched);
  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: usageTypeList?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const onResetFilter = () => {
    const defaultFilters = getDefaultFilter(FilterStateKeys.USAGE_TYPE_LIST);
    setPager(getDefaultPager(FilterStateKeys.USAGE_TYPE_LIST));

    tableRef.current?.resetFilters();
    defaultFilters.forEach((filter) => {
      if (Array.isArray(filter.value)) {
        tableRef.current?.setFilterValue({
          columnKey: filter.key,
          value: new Set(filter.value),
        });
      }
    });

    setFilters(defaultFilters);
  };

  const onSelectEdit = useCallback(
    (id: string) => {
      navigate(getUsageTypeEditRoute(id));
    },
    [navigate],
  );

  const { canCreateSettings } = useSettingsReadOnlyRule();

  const actionBodyTemplate = useCallback(
    (rowData: IUsageTypeResSchema) => {
      const { id, name, status } = rowData;
      if (!canCreateSettings) {
        return null;
      }
      const confirmProps = {
        title: `Are you sure you want to deactivate "${name}"?`,
        description:
          'Once deactivated, this usage type will no longer be available',
      };
      const actions = [
        {
          title: 'Edit',
          enabled: true,
          action: () => onSelectEdit(id),
        },
        {
          title: 'Activate',
          enabled: status === UsageTypeStatusEnum.INACTIVE,
          action: () => doActivateDeactivate({ id, action: 'activate' }),
        },
        {
          title: 'Deactivate',
          color: 'danger',
          confirmProps: {
            ...confirmProps,
            yesButton: 'Deactivate',
          },
          enabled: status === UsageTypeStatusEnum.ACTIVE,
          action: () => doActivateDeactivate({ id, action: 'deactivate' }),
        },
      ];
      return <DataTableActions actions={actions} />;
    },
    [canCreateSettings, doActivateDeactivate, onSelectEdit],
  );

  const hasFiltersOrSearchApplied =
    getFiltersApplied(filters) > 0 || !!searchTerm;

  const searchComponentReset = React.useRef<any>(null);

  const resetInternalFilters = () => {
    searchComponentReset.current && searchComponentReset.current();
  };

  const columnsBase = useMemo(
    () => getColumnsBase(actionBodyTemplate),
    [actionBodyTemplate],
  );

  return (
    <MPageContainer enableAccountSearch>
      <PageHeader />
      <TableHeader
        model="Usage Type"
        showCreateNewBtn={!isTrulyEmptyList && canCreateSettings}
      >
        {!isTrulyEmptyList && (
          <MHStack gap="2">
            <MPageSearchInput
              placeholderKey="Name"
              value={searchTerm}
              onChange={setSearchTerm}
              count={usageTypeList?.totalElements}
              resetSearch={searchComponentReset}
              mode="alwaysExpanded"
            />
            <RefreshTableButton refetch={refetch} />
            <ExportEntityButton
              entity="usageTypes"
              filters={filters}
              searchKey={searchKey}
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
            />
          </MHStack>
        )}
      </TableHeader>

      <DataTable
        ref={tableRef}
        columns={columnsBase}
        data={usageTypeList?.content ?? []}
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={usageTypeList?.totalElements}
        totalPages={usageTypeList?.totalPages}
        pager={pager}
        setPager={setPager}
        initialFilters={filters}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
        }}
        onRowClick={(e) => onSelectEdit(e.id)}
        isLoading={isLoading || (isRefetching && !isFetched)}
        emptyProps={{
          mainMessage: 'Looks like there are no usage types here.',
          smallMessage: null,
          btnLabel: 'New Usage Type',
          to: ROUTES.USAGE_TYPE_NEW,
          secondaryBtnLabel: hasFiltersOrSearchApplied
            ? 'Clear filters'
            : undefined,
          onClickSecondaryBtn: () => {
            resetInternalFilters();
            onResetFilter();
          },
        }}
        emptyFilterResultsProps={{
          btnLabel: 'Reset Filters',
        }}
        resetFilter={() => {
          resetInternalFilters();
          onResetFilter();
        }}
      />
    </MPageContainer>
  );
};

export default UsageTypeListPage;
